import { PaymentTerm } from '../types/invoice';

interface PricingPaymentTerm {
  id: number;
  days: number;
  interest_vat_import_duties: number;
}

interface CompanyInfo {
  pricing_payment_term?: PricingPaymentTerm | PricingPaymentTerm[];
}

interface Invoice {
  payment_term?: PaymentTerm;
  custom_payment_terms?: string;
  booking?: {
    company_info?: CompanyInfo | CompanyInfo[];
  };
}

/**
 * Calculate the effective payment terms that will be used for Moneybird invoice
 * This mirrors the logic in the create-moneybird-invoice edge function
 */
export const calculateEffectivePaymentTerms = (invoice: Invoice) => {
  // Default when company/invoice term is unset: Net 30 (Moneybird + preview).
  let paymentTermDays = 30;
  let paymentConditions = 'Net 30 days';

  // Priority 1: Custom payment terms from invoice
  if (invoice.payment_term === PaymentTerm.CUSTOM && invoice.custom_payment_terms) {
    paymentConditions = invoice.custom_payment_terms;
    // Try to extract days from custom terms (e.g., "15 days net" -> 15)
    const daysMatch = invoice.custom_payment_terms.match(/(\d+)\s*days?/i);
    if (daysMatch) {
      paymentTermDays = parseInt(daysMatch[1], 10);
    }
  } 
  // Priority 2: Standard payment term from invoice
  else if (invoice.payment_term) {
    switch (invoice.payment_term) {
      case PaymentTerm.DAYS_1:
        paymentTermDays = 1;
        paymentConditions = 'Net 1 day';
        break;
      case PaymentTerm.DAYS_7:
        paymentTermDays = 7;
        paymentConditions = 'Net 7 days';
        break;
      case PaymentTerm.DAYS_14:
        paymentTermDays = 14;
        paymentConditions = 'Net 14 days';
        break;
      case PaymentTerm.DAYS_30:
        paymentTermDays = 30;
        paymentConditions = 'Net 30 days';
        break;
      case PaymentTerm.DAYS_60:
        paymentTermDays = 60;
        paymentConditions = 'Net 60 days';
        break;
      case PaymentTerm.IMMEDIATE:
        paymentTermDays = 0;
        paymentConditions = 'Immediate payment';
        break;
      case PaymentTerm.END_OF_MONTH: {
        // Calculate days until end of month
        const currentDate = new Date();
        const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        paymentTermDays = Math.ceil((lastDayOfMonth.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
        paymentConditions = 'End of month';
        break;
      }
      default:
        paymentTermDays = 30;
        paymentConditions = 'Net 30 days';
    }
  } 
  // Priority 3: Company's default payment terms
  else if (invoice.booking?.company_info) {
    const company = Array.isArray(invoice.booking.company_info) 
      ? invoice.booking.company_info[0] 
      : invoice.booking.company_info;

    if (company?.pricing_payment_term) {
      if (Array.isArray(company.pricing_payment_term) && company.pricing_payment_term.length > 0) {
        const companyPaymentTerm = company.pricing_payment_term[0];
        paymentTermDays = companyPaymentTerm.days ?? 30;
        const interestRate = companyPaymentTerm.interest_vat_import_duties || 0;
        paymentConditions = `Payment due within ${paymentTermDays} days${interestRate > 0 ? `, ${interestRate}% interest on import VAT and duties` : ''}`;
      } else if (!Array.isArray(company.pricing_payment_term)) {
        paymentTermDays = company.pricing_payment_term.days ?? 30;
        const interestRate = company.pricing_payment_term.interest_vat_import_duties || 0;
        paymentConditions = `Payment due within ${paymentTermDays} days${interestRate > 0 ? `, ${interestRate}% interest on import VAT and duties` : ''}`;
      }
    }
  }

  // Calculate due date
  const invoiceDate = new Date();
  const dueDate = new Date(invoiceDate);
  dueDate.setDate(dueDate.getDate() + paymentTermDays);

  return {
    days: paymentTermDays,
    conditions: paymentConditions,
    dueDate: dueDate.toISOString().split('T')[0], // YYYY-MM-DD format
    source: invoice.payment_term === PaymentTerm.CUSTOM && invoice.custom_payment_terms 
      ? 'custom'
      : invoice.payment_term 
        ? 'invoice' 
        : 'company'
  };
};

/**
 * Get a human-readable display of payment terms
 */
export const getPaymentTermsDisplay = (invoice: Invoice): string => {
  const effective = calculateEffectivePaymentTerms(invoice);
  
  // Show the source and terms
  const sourceLabel = {
    custom: '🎯 Custom',
    invoice: '📋 Invoice',
    company: '🏢 Company'
  }[effective.source];

  return `${sourceLabel}: ${effective.conditions}`;
};

/**
 * Get payment terms for display in tables/lists
 */
export const getPaymentTermsForDisplay = (invoice: Invoice) => {
  const effective = calculateEffectivePaymentTerms(invoice);
  
  return {
    days: effective.days,
    conditions: effective.conditions,
    dueDate: effective.dueDate,
    source: effective.source,
    shortDisplay: `${effective.days} days`,
    fullDisplay: effective.conditions
  };
}; 